/**
 * Voters — every way the server says no, mapped to a field and a direction.
 *
 * Errors are directions, not verdicts: each message says what to do next. Two backends are live
 * (see `voter-rules.ts`), so both vocabularies are read here — the v2 `errorCode`s and the legacy
 * build's bare English strings — and nothing outside this file string-matches a server message.
 */

import { VOTER_MESSAGES, type VoterField } from './voter-rules';

export type VoterErrorKind =
	| 'duplicate-phone'
	| 'duplicate-vin'
	| 'duplicate-email'
	| 'invalid-pu'
	| 'core-unavailable'
	| 'stale'
	| 'validation'
	| 'forbidden'
	| 'unauthorized'
	| 'rate-limited'
	| 'not-found'
	| 'network'
	| 'unknown';

export interface IDuplicateMatch {
	voterId: string;
	matchedOn: ('phone' | 'vin')[];
	displayName: string;
	maskedPhone: string;
	pollingUnitCode: string;
	source: string;
	importNumber: number | null;
	createdAt: string;
}

export interface IVoterError {
	kind: VoterErrorKind;
	/** The field the message belongs under, when there is one. */
	field?: VoterField;
	message: string;
	status?: number;
	matches?: IDuplicateMatch[];
	/** Seconds, from a 429's Retry-After. */
	retryAfter?: number;
}

export const VOTER_ERROR_COPY = {
	duplicatePhone: 'Another voter already uses this number.',
	duplicateVin: 'This VIN is already registered — open their record instead.',
	duplicateEmail: 'Another voter already uses this email.',
	invalidPu: (code?: string) =>
		code
			? `We couldn't find polling unit ${code}. Check the code or pick it from the list.`
			: "We couldn't find that polling unit. Check the code or pick it from the list.",
	coreUnavailable: 'Polling-unit lookup is unavailable right now. Your entries are still here — try again shortly.',
	stale: 'Someone changed this voter while you were editing. Close and reopen the record to see their changes.',
	forbidden: "You don't have permission to do this. Ask a Super Admin to grant Manage voters.",
	unauthorized: 'Your session has ended. Sign in again — your entries are still here.',
	rateLimited: 'Too many requests in a row. Wait a moment, then try again.',
	notFound: 'That voter no longer exists — it may have been deleted.',
	network: "We couldn't reach the server. Your entries are still here — try again.",
	modelState: 'The server rejected one of the fields. Check them and try again.',
	unknown: 'Something went wrong on the server. Your entries are still here — try again.',
} as const;

type Loose = Record<string, unknown>;

/** Read a property in camelCase or PascalCase — the backends emit both. */
export const pick = (obj: unknown, key: string): unknown => {
	if (!obj || typeof obj !== 'object') return undefined;
	const record = obj as Loose;
	if (key in record) return record[key];
	const pascal = key.charAt(0).toUpperCase() + key.slice(1);
	return record[pascal];
};

const str = (v: unknown): string => (typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v));
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export const readDuplicateMatch = (raw: unknown): IDuplicateMatch => {
	const matchedRaw = pick(raw, 'matchedOn');
	const matchedOn = (Array.isArray(matchedRaw) ? matchedRaw : [matchedRaw])
		.map((m) => str(m).toLowerCase())
		.filter((m): m is 'phone' | 'vin' => m === 'phone' || m === 'vin');
	return {
		voterId: str(pick(raw, 'voterId') ?? pick(raw, 'id')),
		matchedOn,
		displayName: str(pick(raw, 'displayName')),
		maskedPhone: str(pick(raw, 'maskedPhone')),
		pollingUnitCode: str(pick(raw, 'pollingUnitCode')),
		source: str(pick(raw, 'source')),
		importNumber: num(pick(raw, 'importNumber')),
		createdAt: str(pick(raw, 'createdAt')),
	};
};

export const readDuplicateMatches = (raw: unknown): IDuplicateMatch[] => (Array.isArray(raw) ? raw.map(readDuplicateMatch) : []);

/** Map an API reason code (server or client) to the field it concerns. */
const FIELD_FOR_CODE: Record<string, VoterField> = {
	MISSING_FIRST_NAME: 'firstName',
	MISSING_LAST_NAME: 'lastName',
	MISSING_PHONE: 'phoneNumber',
	INVALID_PHONE: 'phoneNumber',
	DUPLICATE_PHONE: 'phoneNumber',
	MISSING_EMAIL: 'email',
	INVALID_EMAIL: 'email',
	MISSING_VIN: 'voterRegistrationNumber',
	INVALID_VIN: 'voterRegistrationNumber',
	DUPLICATE_VIN: 'voterRegistrationNumber',
	INVALID_GENDER: 'gender',
	INVALID_DATE_OF_BIRTH: 'dateOfBirth',
	UNDER_18: 'dateOfBirth',
	MISSING_POLLING_UNIT: 'pollingUnitCode',
	INVALID_POLLING_UNIT: 'pollingUnitCode',
	INVALID_POLLING_UNIT_FORMAT: 'pollingUnitCode',
	DUPLICATE_OVERRIDE_REASON_REQUIRED: 'phoneNumber',
	INVALID_OVERRIDE_REASON: 'phoneNumber',
};

/**
 * The "What to fix" sentence for an import reason code. Unknown codes are SHOWN, never dropped —
 * a new server code must still reach the person fixing the file.
 */
export const describeReasonCode = (code: string, detail?: string): string => {
	switch (code) {
		case 'MISSING_FIRST_NAME':
			return 'Add a first name.';
		case 'MISSING_LAST_NAME':
			return 'Add a last name.';
		case 'MISSING_PHONE':
			return 'Add a phone number.';
		case 'INVALID_PHONE':
			return 'Use an 11-digit Nigerian number, like 08031112233.';
		case 'MISSING_EMAIL':
			return 'Add an email address.';
		case 'INVALID_EMAIL':
			return 'Fix the email address, or leave it blank.';
		case 'MISSING_VIN':
			return 'Add the VIN.';
		case 'INVALID_VIN':
			return 'Fix the VIN — letters and digits only.';
		case 'INVALID_GENDER':
			return 'Use Male or Female, or leave gender blank.';
		case 'INVALID_DATE_OF_BIRTH':
			return 'Use a real date of birth, like 1990-04-17 — not in the future, and under 120 years ago.';
		case 'FIELD_TOO_LONG':
			return detail ? `${detail} is too long — shorten it.` : VOTER_MESSAGES.nameTooLong;
		case 'INVALID_POLLING_UNIT_FORMAT':
			return 'Write the polling-unit code as 4 parts, like 04-09-02-011.';
		case 'INVALID_OVERRIDE_REASON':
			return 'Say why this is a different person — 10 to 500 characters.';
		case 'INVALID_DELETE_REASON':
			return 'Give a reason for deleting — 10 to 500 characters.';
		case 'UNDER_18':
			return 'This voter is under 18 — check the date of birth.';
		case 'MISSING_POLLING_UNIT':
			return 'Add the polling-unit code.';
		case 'INVALID_POLLING_UNIT':
			return detail
				? `Polling unit ${detail} does not exist — check the code.`
				: 'Check the polling-unit code — it does not exist.';
		case 'DUPLICATE_IN_FILE':
			return detail ? `Same phone or VIN as row ${detail} — keep one.` : 'Same phone or VIN as another row — keep one.';
		case 'DUPLICATE_IN_REGISTER':
		case 'DUPLICATE_PHONE':
			return 'Already in the register — nothing to do unless this is a different person.';
		case 'DUPLICATE_VIN':
			return 'This VIN is already registered — nothing to do.';
		case 'DUPLICATE_EMAIL':
			return 'Another voter already uses this email — change it or leave it blank.';
		case 'CORE_UNAVAILABLE':
			return 'Polling-unit lookup was unavailable — upload this row again.';
		default:
			return detail ? `${code}: ${detail}` : `The server rejected this row (${code}).`;
	}
};

/** Duplicates are "already registered", not "to fix" — the voter is in the register either way. */
export const isAlreadyRegisteredCode = (code: string): boolean =>
	code === 'DUPLICATE_IN_REGISTER' || code === 'DUPLICATE_PHONE' || code === 'DUPLICATE_VIN';

/** The legacy build's bare strings → a reason code. `null` when the string is not one of them. */
export const legacyMessageToCode = (message: string): string | null => {
	const m = message.trim().toLowerCase();
	if (!m) return null;
	if (m.includes('phone number already exists')) return 'DUPLICATE_PHONE';
	if (m.includes('voter registration number already exists')) return 'DUPLICATE_VIN';
	if (m.includes('email already exists')) return 'DUPLICATE_EMAIL';
	if (m.includes('invalid polling unit')) return 'INVALID_POLLING_UNIT';
	return null;
};

const statusOf = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status;

const bodyOf = (error: unknown): unknown => (error as { response?: { data?: unknown } })?.response?.data;

const retryAfterOf = (error: unknown): number | undefined => {
	const headers = (error as { response?: { headers?: Record<string, unknown> } })?.response?.headers;
	const raw = headers?.['retry-after'] ?? headers?.['Retry-After'];
	const seconds = Number(raw);
	return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
};

/** `errorCode` may sit at the root of the envelope or inside `data`. */
export const errorCodeOf = (body: unknown): string => {
	const direct = str(pick(body, 'errorCode'));
	if (direct) return direct;
	return str(pick(pick(body, 'data'), 'errorCode'));
};

const messageOf = (body: unknown): string => {
	if (typeof body === 'string') return body;
	return str(pick(body, 'message') ?? pick(body, 'title'));
};

const matchesOf = (body: unknown): IDuplicateMatch[] => {
	const direct = pick(body, 'matches');
	if (Array.isArray(direct)) return readDuplicateMatches(direct);
	return readDuplicateMatches(pick(pick(body, 'data'), 'matches'));
};

const FIELD_NAMES: Record<string, VoterField> = {
	firstname: 'firstName',
	lastname: 'lastName',
	middlename: 'middleName',
	phonenumber: 'phoneNumber',
	email: 'email',
	voterregistrationnumber: 'voterRegistrationNumber',
	gender: 'gender',
	dateofbirth: 'dateOfBirth',
	pollingunitcode: 'pollingUnitCode',
	duplicateoverridereason: 'phoneNumber',
};

const fieldFromName = (name: string): VoterField | undefined => FIELD_NAMES[name.replace(/^\$\./, '').toLowerCase()];

/** `data.errors: [{ field, code, message }]` from VALIDATION_FAILED. */
const validationErrorsOf = (body: unknown): { field: string; code: string; message: string }[] => {
	const list = pick(pick(body, 'data'), 'errors') ?? pick(body, 'errors');
	if (!Array.isArray(list)) return [];
	return list
		.map((e) => ({ field: str(pick(e, 'field')), code: str(pick(e, 'code')), message: str(pick(e, 'message')) }))
		.filter((e) => e.code || e.message);
};

/** ASP.NET's ModelState shape: `{ errors: { Email: ["…"] } }`. First field it names, if ours. */
const modelStateField = (body: unknown): { field?: VoterField; message?: string } => {
	const errors = pick(body, 'errors');
	if (!errors || typeof errors !== 'object') return {};
	const fields: Record<string, VoterField> = {
		firstname: 'firstName',
		lastname: 'lastName',
		middlename: 'middleName',
		phonenumber: 'phoneNumber',
		email: 'email',
		voterregistrationnumber: 'voterRegistrationNumber',
		gender: 'gender',
		dateofbirth: 'dateOfBirth',
		pollingunitcode: 'pollingUnitCode',
	};
	for (const [key, value] of Object.entries(errors as Loose)) {
		const field = fields[key.replace(/^\$\./, '').toLowerCase()];
		if (field) return { field, message: Array.isArray(value) ? str(value[0]) : str(value) };
	}
	return {};
};

/**
 * Turn any thrown request into one `IVoterError`. `context.puCode` lets the invalid-polling-unit
 * message name the code the user actually sent.
 */
export const readVoterError = (error: unknown, context: { puCode?: string } = {}): IVoterError => {
	const status = statusOf(error);
	const body = bodyOf(error);

	if (status === undefined) return { kind: 'network', message: VOTER_ERROR_COPY.network };

	const code = errorCodeOf(body);
	const message = messageOf(body);
	const legacy = legacyMessageToCode(message);
	const effective = code || legacy || '';

	if (effective === 'DUPLICATE_PHONE') {
		return {
			kind: 'duplicate-phone',
			field: 'phoneNumber',
			message: VOTER_ERROR_COPY.duplicatePhone,
			status,
			matches: matchesOf(body),
		};
	}
	if (effective === 'DUPLICATE_VIN') {
		return {
			kind: 'duplicate-vin',
			field: 'voterRegistrationNumber',
			message: VOTER_ERROR_COPY.duplicateVin,
			status,
			matches: matchesOf(body),
		};
	}
	if (effective === 'DUPLICATE_EMAIL')
		return { kind: 'duplicate-email', field: 'email', message: VOTER_ERROR_COPY.duplicateEmail, status };
	if (effective === 'INVALID_POLLING_UNIT') {
		return { kind: 'invalid-pu', field: 'pollingUnitCode', message: VOTER_ERROR_COPY.invalidPu(context.puCode), status };
	}
	if (effective === 'CORE_UNAVAILABLE' || status === 503) {
		return { kind: 'core-unavailable', message: VOTER_ERROR_COPY.coreUnavailable, status };
	}
	if (effective === 'STALE_VOTER') return { kind: 'stale', message: VOTER_ERROR_COPY.stale, status };
	if (effective === 'VOTER_NOT_FOUND') return { kind: 'not-found', message: VOTER_ERROR_COPY.notFound, status };

	if (status === 401) return { kind: 'unauthorized', message: VOTER_ERROR_COPY.unauthorized, status };
	if (status === 403) return { kind: 'forbidden', message: VOTER_ERROR_COPY.forbidden, status };
	if (status === 404) return { kind: 'not-found', message: VOTER_ERROR_COPY.notFound, status };
	if (status === 429)
		return { kind: 'rate-limited', message: VOTER_ERROR_COPY.rateLimited, status, retryAfter: retryAfterOf(error) };

	if (status === 400 || status === 422) {
		// VALIDATION_FAILED carries the per-field verdicts in data.errors[] — the first one leads.
		const first = validationErrorsOf(body)[0];
		if (first) {
			const field = FIELD_FOR_CODE[first.code] ?? fieldFromName(first.field);
			const message = FIELD_FOR_CODE[first.code]
				? describeReasonCode(first.code)
				: first.message || describeReasonCode(first.code, first.field);
			return { kind: 'validation', field, message, status };
		}
		const field = FIELD_FOR_CODE[effective];
		if (field) return { kind: 'validation', field, message: describeReasonCode(effective), status };
		const modelState = modelStateField(body);
		if (modelState.field) {
			return { kind: 'validation', field: modelState.field, message: modelState.message || VOTER_ERROR_COPY.modelState, status };
		}
		return {
			kind: 'validation',
			message: message && !/model state/i.test(message) ? message : VOTER_ERROR_COPY.modelState,
			status,
		};
	}

	return { kind: 'unknown', message: VOTER_ERROR_COPY.unknown, status };
};
